import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ColumnMeta, Row } from '@/lib/biData';
import {
  DATE_PRESETS,
  datePresetRange,
  filterKey,
  EMPTY_FILTERS,
  type BiFilterState,
} from '@/lib/biFilters';

// ============================================================
// Zifek BI — panneau de filtres interactifs.
//  • Filtre de date avec périodes prédéfinies + plage personnalisée.
//  • Filtres d'attributs (catégorie, ville, produit…) par valeur.
//  • Les filtres s'appliquent à tous les graphiques du dashboard
//    (cross-filtering via le clic sur un graphique).
// ============================================================

interface FiltersPanelProps {
  columns: ColumnMeta[];
  rows: Row[];
  state: BiFilterState;
  onChange: (next: BiFilterState) => void;
  filteredCount: number;
  totalCount: number;
  datasetId?: number | null;
  datasetName?: string;
  commerceId?: string;
}

interface SavedFilter {
  id: number;
  name: string;
  state: BiFilterState;
}

export default function FiltersPanel({
  columns,
  rows,
  state,
  onChange,
  filteredCount,
  totalCount,
  datasetId = null,
  datasetName = '',
  commerceId = '',
}: FiltersPanelProps) {
  const dateColumns = useMemo(
    () => columns.filter((c) => c.type === 'date').map((c) => c.name),
    [columns],
  );
  const dateColOptions = dateColumns.length > 0 ? dateColumns : columns.map((c) => c.name);

  const dimensionColumns = useMemo(
    () =>
      columns
        .filter((c) => (c.type === 'string' || c.type === 'boolean' || c.type === 'date') && c.uniqueCount > 1 && c.uniqueCount <= 60)
        .map((c) => c.name),
    [columns],
  );

  const [activeCol, setActiveCol] = useState(dimensionColumns[0] || '');

  // ── Filtres favoris sauvegardés (mémorisés par source de données) ──
  const [saved, setSaved] = useState<SavedFilter[]>([]);
  const [saveName, setSaveName] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);

  const loadSaved = useCallback(async () => {
    if (datasetId == null) { setSaved([]); return; }
    try {
      const { data } = await supabase
        .from('bi_saved_filters')
        .select('id, name, state')
        .eq('dataset_id', datasetId)
        .order('created_at', { ascending: false });
      setSaved((data || []).map((r: { id: number; name: string; state: BiFilterState }) => ({
        id: r.id,
        name: r.name,
        state: r.state || EMPTY_FILTERS,
      })));
    } catch { setSaved([]); }
  }, [datasetId]);

  useEffect(() => { void loadSaved(); }, [loadSaved]);

  const saveCurrent = async () => {
    const name = saveName.trim();
    if (!name || datasetId == null) return;
    setSavingFilter(true);
    try {
      const { data, error } = await supabase
        .from('bi_saved_filters')
        .insert({ idcommerce: commerceId, dataset_id: datasetId, name, state })
        .select('id, name, state')
        .single();
      if (error) throw error;
      if (data) setSaved((prev) => [{ id: data.id, name: data.name, state: data.state || state }, ...prev]);
      setSaveName('');
    } catch { /* best-effort */ } finally {
      setSavingFilter(false);
    }
  };

  const removeSaved = async (id: number) => {
    try {
      await supabase.from('bi_saved_filters').delete().eq('id', id);
      setSaved((prev) => prev.filter((f) => f.id !== id));
    } catch { /* best-effort */ }
  };

  const values = useMemo(() => {
    if (!activeCol) return [];
    const set = new Set<string>();
    rows.forEach((r) => {
      const v = r[activeCol];
      if (v !== null && v !== undefined && v !== '') set.add(String(v));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr')).slice(0, 60);
  }, [rows, activeCol]);

  const activeKeys = new Set(state.filters.map((f) => f.id));

  const toggleValue = (column: string, value: string) => {
    const id = filterKey(column, value);
    const exists = state.filters.some((f) => f.id === id);
    const filters = exists
      ? state.filters.filter((f) => f.id !== id)
      : [...state.filters, { id, column, value }];
    onChange({ ...state, filters });
  };

  const setDateColumn = (column: string) => {
    if (!column) {
      onChange({ ...state, date: null });
      return;
    }
    const preset = state.date?.preset || 'this_month';
    const range = datePresetRange(preset);
    onChange({ ...state, date: { column, preset, from: range.from, to: range.to } });
  };

  const applyPreset = (preset: string) => {
    if (!state.date) return;
    const range = datePresetRange(preset);
    onChange({ ...state, date: { ...state.date, preset, from: range.from, to: range.to } });
  };

  const setCustomDate = (patch: Partial<{ from: string; to: string }>) => {
    if (!state.date) return;
    onChange({ ...state, date: { ...state.date, preset: 'custom', ...patch } });
  };

  return (
    <div className="space-y-5">
      {/* Résumé */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-filter-3-line"></i>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-900">
            {filteredCount.toLocaleString('fr-FR')} / {totalCount.toLocaleString('fr-FR')} lignes
          </p>
          <p className="text-xs text-foreground-500">
            {state.filters.length + (state.date ? 1 : 0) === 0
              ? 'Aucun filtre actif — tout le jeu de données est affiché.'
              : `${state.filters.length + (state.date ? 1 : 0)} filtre(s) appliqué(s) à tous les graphiques.`}
          </p>
        </div>
        {(state.filters.length > 0 || state.date) && (
          <button
            onClick={() => onChange({ filters: [], date: null })}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-circle-line"></i>Réinitialiser
          </button>
        )}
      </div>

      {/* Filtres actifs */}
      {state.filters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">Actifs :</span>
          {state.filters.map((f) => (
            <button
              key={f.id}
              onClick={() => onChange({ ...state, filters: state.filters.filter((x) => x.id !== f.id) })}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200/70 transition-colors cursor-pointer"
              title={`${f.column} = ${f.value}`}
            >
              <span className="max-w-[160px] truncate">{f.column} : {f.value}</span>
              <i className="ri-close-line text-xs"></i>
            </button>
          ))}
        </div>
      )}

      {/* Filtre de date */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-calendar-line text-secondary-500"></i>Période
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1">Colonne de date</label>
            <select
              value={state.date?.column || ''}
              onChange={(e) => setDateColumn(e.target.value)}
              className="w-full px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50 cursor-pointer"
            >
              <option value="">— Aucune —</option>
              {dateColOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {state.date && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1">Du</label>
                <input
                  type="date"
                  value={state.date.from}
                  onChange={(e) => setCustomDate({ from: e.target.value })}
                  className="w-full px-2.5 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1">Au</label>
                <input
                  type="date"
                  value={state.date.to}
                  onChange={(e) => setCustomDate({ to: e.target.value })}
                  className="w-full px-2.5 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50"
                />
              </div>
            </div>
          )}
        </div>
        {state.date && (
          <div className="flex flex-wrap gap-1.5">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => applyPreset(p.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  state.date?.preset === p.id
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filtres d'attributs */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500"></i>Filtres par catégorie
        </h3>
        {dimensionColumns.length === 0 ? (
          <p className="text-xs text-foreground-400">Aucune colonne catégorielle exploitable.</p>
        ) : (
          <>
            <div className="mb-3">
              <label className="block text-[11px] font-medium text-foreground-600 mb-1">Colonne</label>
              <select
                value={activeCol}
                onChange={(e) => setActiveCol(e.target.value)}
                className="w-full sm:w-72 px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50 cursor-pointer"
              >
                {dimensionColumns.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
              {values.map((v) => {
                const active = activeKeys.has(filterKey(activeCol, v));
                return (
                  <button
                    key={v}
                    onClick={() => toggleValue(activeCol, v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer max-w-[220px] truncate ${
                      active
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                    title={v}
                  >
                    {active && <i className="ri-check-line mr-1"></i>}
                    {v}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Filtres sauvegardés (favoris par dashboard) */}
      {datasetId != null && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
            <i className="ri-star-line text-accent-500"></i>Filtres sauvegardés
          </h3>
          <p className="text-xs text-foreground-400 mb-3">
            Mémorisez une combinaison de filtres pour la réutiliser en un clic{datasetName ? ` sur « ${datasetName} »` : ''}.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="Nom du filtre favori…"
              className="flex-1 px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50"
            />
            <button
              onClick={saveCurrent}
              disabled={savingFilter || !saveName.trim()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-foreground-800 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {savingFilter ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-bookmark-line"></i>}
              Enregistrer
            </button>
          </div>
          {saved.length === 0 ? (
            <p className="text-xs text-foreground-400">Aucun filtre sauvegardé pour l&apos;instant.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {saved.map((f) => (
                <div key={f.id} className="flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-accent-100 text-accent-800">
                  <button
                    onClick={() => onChange(f.state)}
                    className="flex items-center gap-1.5 text-xs font-medium whitespace-nowrap cursor-pointer"
                    title="Appliquer ce filtre sauvegardé"
                  >
                    <i className="ri-filter-3-line"></i>{f.name}
                  </button>
                  <button
                    onClick={() => removeSaved(f.id)}
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-accent-200/70 transition-colors cursor-pointer"
                    title="Supprimer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
        <i className="ri-information-line"></i>
        Astuce : cliquez sur une barre ou une part d&apos;un graphique pour filtrer instantanément tous les autres.
      </p>
    </div>
  );
}